import React, { useMemo, useState } from "react";
import {
  MdThumbDown,
  MdThumbUp,
  MdOutlineThumbDownAlt,
  MdOutlineThumbUpAlt,
} from "react-icons/md";

import { CommunityMemberAvatar } from "@/client_app/components/CommunityMemberAvatar";
import { Checkbox } from "@/ui/Checkbox";

export interface FeedbackParams {
  positionId: string;
  isLiked: boolean;
  categories: string[];
  review: string;
  rehire: boolean;
}
interface Props {
  profileImageUrl: string;
  fullName: string;
  positionId: string;
  onChange: (value: FeedbackParams) => void;
}

const tags = [
  { label: "Skills", value: "skills" },
  { label: "Communication", value: "communication" },
  { label: "Professionalism", value: "professionalism" },
  { label: "Role Suitability", value: "role_suitability" },
  { label: "Performance", value: "performance" },
];
export const FeedbackItem = ({
  fullName,
  onChange,
  positionId,
  profileImageUrl,
}: Props): JSX.Element => {
  const [isLiked, setIsLiked] = useState(true);
  const [rehire, setRehire] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [additionalNotes, setAdditionalNotes] = useState("");

  const toggleTag = (value) => {
    setSelectedTags((prev) => {
      const newValue = prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value];
      onChange({
        positionId,
        isLiked,
        categories: newValue,
        review: additionalNotes,
        rehire,
      });
      return newValue;
    });
  };

  const toggleLike = (value: boolean) => {
    onChange({
      positionId,
      isLiked: value,
      categories: selectedTags,
      review: additionalNotes,
      rehire,
    });
    setIsLiked(value);
  };

  const handleOnReviewChange = (event) => {
    const newValue = event.target.value;
    onChange({
      positionId,
      isLiked,
      categories: selectedTags,
      review: newValue,
      rehire,
    });
    setAdditionalNotes(newValue);
  };
  return (
    <div className='flex justify-center items-center flex-wrap gap-4'>
      <CommunityMemberAvatar
        profileImageUrl={profileImageUrl}
        name={fullName}
        size='SMALL'
      />
      <div className='font-bold'>How was your experience with {fullName} ?</div>
      <div className='flex pt-2 gap-2'>
        {isLiked ? (
          <>
            <MdThumbUp className='cursor-pointer bg-gray-100 rounded-full h-10 w-10 p-2 text-primary-800' />
            <MdOutlineThumbDownAlt
              className='cursor-pointer bg-gray-100 rounded-full h-10 w-10 p-2'
              onClick={() => toggleLike(false)}
            />
          </>
        ) : (
          <>
            <MdOutlineThumbUpAlt
              className='cursor-pointer bg-gray-100 rounded-full h-10 w-10 p-2'
              onClick={() => toggleLike(true)}
            />
            <MdThumbDown className='cursor-pointer bg-gray-100 rounded-full h-10 w-10 p-2 text-error-400' />
          </>
        )}
      </div>
      <div className='w-full'>Select all that apply:</div>
      <div>
        {tags.map(({ label, value }) => (
          <button
            key={value}
            className={`btn btn-sm rounded-full font-bold text-secondary-700 m-1 ${
              selectedTags.includes(value)
                ? isLiked
                  ? "bg-primary-50"
                  : "bg-error-200"
                : "bg-gray-100"
            }`}
            onClick={() => toggleTag(value)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className='text-left'>
        <div>Write a review (optional):</div>
        <div className='pt-2'>
          <textarea
            onChange={handleOnReviewChange}
            cols={100}
            className='w-full p-3 border-neutral-300 border rounded-lg'
          />
        </div>
        <div className='flex items-center'>
          <Checkbox
            id='rehire'
            checked={rehire}
            onChange={() => {
              const newValue = !rehire;
              onChange({
                positionId,
                isLiked,
                categories: selectedTags,
                review: additionalNotes,
                rehire: newValue,
              });
              setRehire(newValue);
            }}
          />
          <label htmlFor='rehire' className='pl-2'>
            Would hire again
          </label>
        </div>
      </div>
    </div>
  );
};
